import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import { getCopy, postOrderFeedback } from 'shared/models/api';
import { trackNow } from 'shared/models/analytic';
import {
  uppercase,
  nonresponsiveTitle,
  secondary,
  sansSerifLight,
  fontSizeS,
  lineHeightM
} from 'scy/text/styles.mod.scss';
import { SecondaryH5 } from 'scy/text';
import {
  TextButton,
  PrimaryButton,
  SecondaryButton,
  LoadingButton
} from 'scy/buttons';
import ContinueButton from 'scy/buttons/ContinueButton';
import { DangerWarning } from 'scy/Warning';
import ThumbsDown from 'images/icons/thumbs-down.svg?react-svg';
import ThumbsUp from 'images/icons/thumbs-up.svg?react-svg';
import { flex, centerContent } from 'scy/flex.mod.scss';
import { inlineBlock } from 'scy/display.mod.scss';
import margin from 'scy/margin.mod.scss';
import styles from './RateExperienceForm.mod.scss';
import { formContainer, textareaInput, primaryButton } from './styles.mod.scss';

export default function RateExperienceForm({ orderId, handleSuccess }) {
  const [rating, setRating] = useState(null);
  const [negativeOptions, setNegativeOptions] = useState([]);
  const [positiveOptions, setPositiveOptions] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function pickThumbsUp() {
    setReasons([]);
    setRating('up');
  }

  function pickThumbsDown() {
    setReasons([]);
    setRating('down');
  }

  function selectReason(reason) {
    setReasons(rs => [reason, ...rs]);
  }

  function deselectReason(reason) {
    setReasons(reasons => reasons.filter(r => r !== reason));
    if (reason === 'OTHER') setOtherReason('');
  }

  useEffect(() => {
    getCopy()
      .then(copy => {
        const feedback = (copy && copy.feedback) || {};
        const positive = feedback.positive || [];
        const negative = feedback.negative || [];
        setNegativeOptions(negative);
        setPositiveOptions(positive);
      })
      .catch(err => console.log(err)); // eslint-disable-line no-console
  }, []);

  useEffect(() => {
    trackNow('Saucey Rating Started', { ['order ID']: orderId });
  }, [orderId]);

  const showOther = reasons.includes('OTHER');

  const otherReasonRef = useRef();

  useEffect(() => {
    if (showOther && otherReasonRef.current) {
      scrollIntoView(otherReasonRef.current, {
        time: 300,
        align: { topOffset: 180 }
      });
    }
  }, [showOther]);

  function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    let trackParams = {};
    if (rating === 'up') trackParams.rating = 'good';
    if (rating === 'down') trackParams.rating = 'bad';
    if (reasons.length) trackParams.reason = reasons;
    trackNow('Saucey Rating Completed', trackParams);

    return postOrderFeedback(orderId, {
      positive: rating === 'up' ? reasons : [],
      negative: rating === 'down' ? reasons : [],
      notes: showOther && otherReason,
      thumb: rating === 'up' ? 1 : rating === 'down' ? 2 : 0
    })
      .then(() => {
        setLoading(false);
        handleSuccess();
      })
      .catch(({ message }) => {
        setLoading(false);
        setError(message);
      });
  }

  return (
    <>
      <h1
        className={classnames(
          margin.top_xs,
          margin.bottom_md,
          margin.tablet_top_xl,
          nonresponsiveTitle,
          uppercase
        )}
      >
        Rate Your Experience
      </h1>
      <h2 className={styles.subhead}>Tell us about your Saucey experience.</h2>

      <form
        className={classnames(formContainer, margin.bottom_lg)}
        onSubmit={handleSubmit}
      >
        <div className={classnames(flex, centerContent, margin.bottom_md)}>
          <div
            className={classnames(
              styles.leftThumbContainer,
              rating === 'down'
                ? styles.activeThumbsDownContainer
                : styles.inactiveThumbContainer
            )}
          >
            <TextButton
              type="button"
              noBorder
              displayClass={inlineBlock}
              className={styles.thumbsDown}
              onClick={pickThumbsDown}
            >
              <ThumbsDown width={24} height={24} />
            </TextButton>
          </div>
          <div
            className={classnames(
              styles.inactiveThumbContainer,
              rating === 'up'
                ? styles.activeThumbsUpContainer
                : styles.inactiveThumbContainer
            )}
          >
            <TextButton
              type="button"
              noBorder
              displayClass={inlineBlock}
              className={styles.thumbsUp}
              onClick={pickThumbsUp}
            >
              <ThumbsUp width={24} height={25} />
            </TextButton>
          </div>
        </div>

        {rating === null ? (
          <p
            className={classnames(
              secondary,
              sansSerifLight,
              fontSizeS,
              lineHeightM
            )}
          >
            This rating will not affect your courier.
          </p>
        ) : rating === 'up' ? (
          <>
            <SecondaryH5 uppercase>Why was your experience great?</SecondaryH5>
            <div className={styles.pillContainer}>
              {positiveOptions.map(option => (
                <Reason
                  key={option}
                  text={option}
                  select={selectReason}
                  deselect={deselectReason}
                  isSelected={reasons.includes(option)}
                />
              ))}
            </div>
          </>
        ) : (
          rating === 'down' && (
            <>
              <SecondaryH5 uppercase>Why was your experience poor?</SecondaryH5>
              <div className={styles.pillContainer}>
                {negativeOptions.map(option => (
                  <Reason
                    key={option}
                    text={option}
                    select={selectReason}
                    deselect={deselectReason}
                    isSelected={reasons.includes(option)}
                  />
                ))}
              </div>
            </>
          )
        )}

        {showOther && (
          <textarea
            placeholder="Tell us more…"
            className={textareaInput}
            name="otherReason"
            autoFocus
            ref={otherReasonRef}
            value={otherReason || ''}
            onChange={e => setOtherReason(e.target.value)}
          />
        )}

        {rating !== null && (
          <ContinueButton
            Button={LoadingButton}
            type="submit"
            className={primaryButton}
            loading={loading}
          >
            Share Feedback
          </ContinueButton>
        )}

        {error && (
          <DangerWarning className={margin.top_md}>{error}</DangerWarning>
        )}
      </form>
    </>
  );
}

function Reason({ text, select, deselect, isSelected }) {
  return isSelected ? (
    <PrimaryButton
      type="button"
      onClick={() => deselect(text)}
      className={styles.pill}
    >
      {text}
    </PrimaryButton>
  ) : (
    <SecondaryButton
      type="button"
      onClick={() => select(text)}
      className={classnames(styles.pill, styles.unselectedPill)}
    >
      {text}
    </SecondaryButton>
  );
}
